/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import type { ReactNode } from "react";

import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface CTAButtonProps {
  /** Button text */
  children: ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Button variant */
  variant?: "primary" | "outline";
  /** Show arrow icon */
  showArrow?: boolean;
  /** Additional className */
  className?: string;
  /** Button size (default: "lg") */
  size?: "default" | "sm" | "lg" | "icon";
  /** Disabled state */
  disabled?: boolean;
}

/**
 * CTAButton - Landing page call-to-action button with Neutral Gold theming
 *
 * Primary variant uses gold (secondary) background
 * Outline variant uses foreground border
 */
export function CTAButton({
  children,
  onClick,
  variant = "primary",
  showArrow = false,
  className = "",
  size = "lg",
  disabled = false,
}: CTAButtonProps) {
  if (variant === "primary") {
    return (
      <Button
        size={size}
        className={cn(
          "bg-secondary text-secondary-foreground font-semibold shadow-md transition-standard hover:bg-secondary/90 hover:shadow-lg hover-lift",
          className,
        )}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
        {showArrow && <ArrowRight className="ml-2 h-5 w-5" />}
      </Button>
    );
  }

  // Outline variant — uses white border on dark backgrounds
  return (
    <Button
      size={size}
      variant="outline"
      className={cn(
        "border-2 border-white/40 text-white bg-white/10 font-semibold hover:bg-white/20",
        className,
      )}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
      {showArrow && <ArrowRight className="ml-2 h-5 w-5" />}
    </Button>
  );
}
